import type { NavGroup, NavLeaf } from "./nav";
import {
  ADD_PROVIDER_PATH,
  CHANGE_ONU_PATH,
  COMPONENTS_PATH,
  CREATE_PLAN_PATH,
  CREATE_SUBSCRIBER_PATH,
  CUSTOMER_LOOKUP_PATH,
  DIAGNOSE_USER_PATH,
  RADIUS_TEST_PATH,
  PAYMENT_GATEWAY_PATH,
  UPI_NOTICES_PATH,
  DISABLE_USER_PATH,
  ON_SALE_PATH,
  PIN_ORDERS_PATH,
  PIN_PLANS_PATH,
  PRICE_LIST_PATH,
  PRINT_VOUCHER_PATH,
  PROMO_CODES_PATH,
  PROMOTIONS_PATH,
  RECHARGE_PATH,
  SECURITY_PATH,
  SETTINGS_PATH,
  STAFF_PATH,
  SUBSCRIBER_PROFILES_PATH,
} from "./nav";

/**
 * The menu search in the header (owner 2026-10-22: "the main menu with sub
 * menus is getting longer and longer"). Type "funds", "pin", "radius test" or
 * "operator email" and get the screen, WHERE it sits in the menu, and what it
 * is for.
 *
 * ⚠️ It searches only the leaves this login is already shown (navGroups and the
 * account menu), so it never offers a screen the sidebar hides. The words
 * below are extra ways to find a screen, never a way to add one.
 */
export interface SearchHit {
  leaf: NavLeaf;
  /** Where it sits: "Providers › Pins/Funds", or "Account › Edit profile". */
  where: string;
  /** One line of guidance: what the screen is for. */
  hint?: string;
}

interface Help {
  words: string;
  hint: string;
}

/** Extra words and a hint per screen, by path. A screen not listed is found by its menu label alone. */
const HELP: Record<string, Help> = {
  [PIN_ORDERS_PATH]: { words: "funds fund wallet balance add funds top up pin orders buy pins history", hint: "Buy pins, see your pins, and your Funds: balance, Add funds, history." },
  [PIN_PLANS_PATH]: { words: "pin plan voucher price sub-operator", hint: "The plans your sub-operators can buy as pins." },
  [ADD_PROVIDER_PATH]: { words: "add provider sub-operator franchise new operator isp vno", hint: "Add an operator beneath you." },
  [CREATE_SUBSCRIBER_PATH]: { words: "new user add subscriber customer onu mac install", hint: "Add a subscriber with its plan and ONU MAC." },
  [CUSTOMER_LOOKUP_PATH]: { words: "find user search subscriber customer mobile username", hint: "Find a subscriber by mobile, name or username." },
  [PAYMENT_GATEWAY_PATH]: { words: "payment gateway razorpay cashfree upi qr upi id key secret online payment collect money recharge", hint: "Your own Razorpay account or UPI ID, where subscribers' recharges are paid." },
  [UPI_NOTICES_PATH]: { words: "upi payment notice utr reference paid manual apply reject recharge qr", hint: "Subscribers who paid your UPI ID: check the reference, then Apply or Reject." },
  [RADIUS_TEST_PATH]: { words: "radius test login password check cannot sign in wrong password auth accept reject tools", hint: "Test a subscriber's username and password against RADIUS and see why it is refused." },
  [DIAGNOSE_USER_PATH]: { words: "diagnose radius login problem cannot connect session auth tools", hint: "Check why a subscriber cannot sign in." },
  [CHANGE_ONU_PATH]: { words: "device mac onu change replace", hint: "Replace a subscriber's ONU / device MAC." },
  [DISABLE_USER_PATH]: { words: "disable block suspend user", hint: "Stop a subscriber's access (kept, not deleted)." },
  [SUBSCRIBER_PROFILES_PATH]: { words: "invoice preference gst profile", hint: "How each subscriber is invoiced." },
  [RECHARGE_PATH]: { words: "recharge renew top up plan payment", hint: "Recharge a subscriber's plan." },
  [PRINT_VOUCHER_PATH]: { words: "voucher print slip receipt", hint: "Print a voucher slip from a recharge." },
  "/billing/record-payment": { words: "payment receipt cash collect", hint: "Record a payment you received." },
  "/billing/issued": { words: "invoice issued email bill", hint: "Invoices you issued and whether they were emailed." },
  "/billing/invoice-name": { words: "invoice name gstin branch upstream consent licence own name series agr", hint: "Whose name is on your internet invoices, and requests to use yours." },
  "/billing/upstream": { words: "upstream supplier bandwidth commitment mbps purchase input gst itc", hint: "What you buy from your bandwidth supplier, and its invoices." },
  "/billing/customers": { words: "ankh customer operator company raise invoice gst account", hint: "Ankh's customers: open a billing account, raise an invoice." },
  "/platform": { words: "platform tenant invite invitation promotion trial suspend restrict overdue", hint: "io.ankhos tenants, their state, invitations and promotions." },
  "/billing/invoices": { words: "ankh invoice bill owe pay", hint: "What Ankh billed you; view, download or pay." },
  [PRICE_LIST_PATH]: { words: "price rate tariff", hint: "Your prices per component." },
  [COMPONENTS_PATH]: { words: "component speed data validity", hint: "The building blocks of a plan." },
  [CREATE_PLAN_PATH]: { words: "new plan add plan build", hint: "Build a new plan." },
  [ON_SALE_PATH]: { words: "edit plan view plan close plan on sale", hint: "See or change a plan on sale." },
  [PROMOTIONS_PATH]: { words: "offer festival discount", hint: "Festive offers." },
  [PROMO_CODES_PATH]: { words: "promo code coupon discount", hint: "Codes subscribers enter when they pay." },
  [STAFF_PATH]: { words: "staff login invite employee role operator email password", hint: "Invite staff and give each a role." },
  [SECURITY_PATH]: { words: "password otp authenticator 2fa two-step security sign-in", hint: "Your own sign-in: password and authenticator app." },
  [SETTINGS_PATH]: { words: "preferences logo settings", hint: "Your console preferences." },
  "/operator/profile": { words: "operator email mobile profile address gst pan legal name password", hint: "Your operator details, email and password." },
  "/operator/legal": { words: "terms privacy policy legal documents", hint: "Your terms and policies." },
  "/portal/appearance": { words: "theme colour color hotspot login page portal", hint: "How your hotspot login page looks." },
  "/roster": { words: "member roster sponsor organisation", hint: "Sponsored members." },
  "/reports/sms-log": { words: "sms message otp delivery", hint: "SMS sent and whether they arrived." },
  "/reports/expired-not-renewed": { words: "expired lapsed churn win back renew not renewed call list", hint: "Plans that ended and were not renewed." },
  "/reports/failed-logins": { words: "failed login reject wrong password cannot connect radius", hint: "Rejected logins per username." },
  "/reports/collections": { words: "collection takings revenue income cash online money daily monthly", hint: "Money taken per day or month by method." },
  "/reports/funds-statement": { words: "funds statement balance ledger sub-operator wallet top up", hint: "Funds opening, in, out and closing per sub-operator." },
  "/reports/renewals-by-plan": { words: "renewal renew repeat retention plan", hint: "Renewals and first recharges per plan." },
  "/reports/never-used": { words: "never used inactive idle not using no session churn", hint: "Paying subscribers with no sessions." },
  "/reports/top-usage": { words: "top usage data gb heavy bandwidth consumption", hint: "Heaviest users by data." },
  "/reports/peak-hours": { words: "peak hours busy time load capacity maintenance", hint: "Sessions by hour of day." },
  "/reports/payment-failures": { words: "payment failed failure online razorpay gateway pending", hint: "Online payments that did not complete." },
  "/reports/promo-use": { words: "promo promotion code coupon discount redemption", hint: "Promotion code redemptions." },
  "/reports/base-snapshot": { words: "base snapshot total subscribers count active expired disabled", hint: "Your subscriber base right now." },
  "/reports/staff-activity": { words: "staff activity who did audit employee operator login", hint: "What each login recorded." },
  "/reports/tenant-summary": { words: "tenant summary platform customers subscribers sms", hint: "Platform tenants at a glance." },
  "/reports/new-subscribers": { words: "new subscriber signup sign-up registration added growth", hint: "Subscribers registered in a period." },
  "/reports/active-sessions": { words: "online connected now session", hint: "Who is online now." },
  "/reports/expiring-plans": { words: "expire expiry due renew", hint: "Plans ending soon." },
  "/reports/active-plans": { words: "active subscription running plan current", hint: "Every plan running now." },
  "/reports/recharge-log": { words: "recharge log history payment receipt sale", hint: "Every recharge in a period." },
  "/reports/recharge-log-by-user": { words: "recharge history one subscriber username", hint: "One subscriber's recharges." },
  "/reports/user-sessions": { words: "session history one subscriber username login logout", hint: "One subscriber's sessions." },
  "/reports/usage": { words: "usage online session data peak login reports", hint: "Who is online, data used, busy hours, failed logins." },
  "/reports/subscribers": { words: "subscriber plan expiry renewal new base reports", hint: "Plans running, expiring and lapsed; new subscribers; renewals." },
  "/reports/money": { words: "money collection recharge funds payment promo revenue reports", hint: "Collections, recharges, funds, failed payments, promo codes." },
  "/reports/operations": { words: "sms staff audit tenant operations reports", hint: "SMS sent, what each staff login did." },
};

/** The one line of guidance for a screen, if it has one: the category page shows it on each card. */
export function hintFor(path: string): string | undefined {
  return HELP[path]?.hint;
}

function words(s: string): string[] {
  return s.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

/**
 * Every query word must start some word of the screen's label, its group, or
 * its extra words. Label hits rank first. At most `limit` hits.
 */
export function searchNav(groups: NavGroup[], account: NavLeaf[], query: string, limit = 8): SearchHit[] {
  const q = words(query);
  if (!q.length) return [];
  const entries = [
    // A Reports category is found itself, and so is every report in it, by
    // its own name: the sidebar shows four categories, the search all twenty.
    ...groups.flatMap((g) =>
      g.items.flatMap((leaf) => [{ leaf, group: g.label }, ...(leaf.sub ?? []).map((r) => ({ leaf: r, group: `${g.label} › ${leaf.label}` }))]),
    ),
    ...account.map((leaf) => ({ leaf, group: "Account" })),
  ];
  const seen = new Set<string>();
  const scored: { hit: SearchHit; score: number }[] = [];
  for (const { leaf, group } of entries) {
    if (seen.has(leaf.to) || leaf.disabled) continue;
    const help = HELP[leaf.to];
    const label = words(leaf.label);
    const all = [...label, ...words(group), ...words(help?.words ?? "")];
    if (!q.every((w) => all.some((x) => x.startsWith(w)))) continue;
    seen.add(leaf.to);
    const score = q.filter((w) => label.some((x) => x.startsWith(w))).length;
    scored.push({ hit: { leaf, where: `${group} › ${leaf.label}`, ...(help ? { hint: help.hint } : {}) }, score });
  }
  return scored
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.hit);
}
